import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from '../../theme/useTheme';
import type { TonyteTheme } from '../../theme/types';

// Orange/gold star colour, plus the review palette for Midnight / Daylight.
export const STAR_GOLD = '#FFB938';
export function reviewPalette(theme: TonyteTheme) {
  if (theme.isDark) {
    return {
      text: '#FFFFFF',
      muted: 'rgba(255,255,255,0.62)',
      faint: 'rgba(255,255,255,0.42)',
      surface: 'rgba(255,255,255,0.06)',
      surfaceStrong: 'rgba(255,255,255,0.1)',
      border: 'rgba(255,255,255,0.12)',
      starEmpty: 'rgba(255,255,255,0.22)',
      accent: '#FF9A3C',
      sheet: 'rgba(12,14,24,0.98)',
      input: 'rgba(255,255,255,0.06)',
      track: 'rgba(255,255,255,0.1)',
    };
  }
  return {
    text: '#211D19',
    muted: '#62584E',
    faint: '#8A7D70',
    surface: 'rgba(255,252,247,0.90)',
    surfaceStrong: 'rgba(239,230,219,0.96)',
    border: 'rgba(105,88,70,0.24)',
    starEmpty: '#B7AA9B',
    accent: '#C95F12',
    sheet: '#FFFCF7',
    input: 'rgba(243,235,225,0.92)',
    track: 'rgba(105,88,70,0.18)',
  };
}

const STAR_PATH = 'M12 2.6l2.9 5.88 6.5.95-4.7 4.58 1.11 6.47L12 17.43l-5.81 3.05 1.11-6.47-4.7-4.58 6.5-.95z';

function Star({ size, color }: { size: number; color: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d={STAR_PATH} fill={color} />
    </Svg>
  );
}

// Read-only stars; fractional values fill part of a star (4.6 → 4 full stars
// and 60% of the fifth).
export function StarRow({ value, size = 14, gap = 2, emptyColor }: { value: number; size?: number; gap?: number; emptyColor?: string }) {
  const { theme } = useTheme();
  const empty = emptyColor || reviewPalette(theme).starEmpty;
  const clamped = Math.max(0, Math.min(5, value));
  return (
    <View style={[styles.row, { gap }]} accessible={false} importantForAccessibility="no-hide-descendants">
      {[0, 1, 2, 3, 4].map(index => {
        const fill = Math.max(0, Math.min(1, clamped - index));
        return (
          <View key={index} style={{ width: size, height: size }} testID={`star-fill-${Math.round(fill * 100)}`}>
            <Star size={size} color={empty} />
            {fill > 0 ? (
              <View style={[StyleSheet.absoluteFill, styles.clip, { width: size * fill }]}>
                <Star size={size} color={STAR_GOLD} />
              </View>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

// Tappable 1–5 picker (screen readers get an adjustable control).
export function StarPicker({ value, onChange, size = 38, label }: { value: number; onChange: (value: number) => void; size?: number; label: string }) {
  const { theme } = useTheme();
  const empty = reviewPalette(theme).starEmpty;
  return (
    <View
      style={[styles.row, styles.picker]}
      accessible
      accessibilityRole="adjustable"
      accessibilityLabel={label}
      accessibilityValue={{ min: 0, max: 5, now: value, text: `${value}/5` }}
      accessibilityActions={[{ name: 'increment' }, { name: 'decrement' }]}
      onAccessibilityAction={event => {
        if (event.nativeEvent.actionName === 'increment') onChange(Math.min(5, value + 1));
        if (event.nativeEvent.actionName === 'decrement') onChange(Math.max(1, value - 1));
      }}
    >
      {[1, 2, 3, 4, 5].map(star => (
        <Pressable key={star} onPress={() => onChange(star)} hitSlop={6} testID={`star-pick-${star}`} style={styles.pickerStar}>
          <Star size={size} color={star <= value ? STAR_GOLD : empty} />
        </Pressable>
      ))}
    </View>
  );
}

// "4.6" / "4,6" — the average to one decimal in the app language.
export function formatAverage(value: number, language: string) {
  const fixed = (Math.round(value * 10) / 10).toFixed(1);
  return language === 'en' ? fixed : fixed.replace('.', ',');
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center' },
  picker: { gap: 8, justifyContent: 'center' },
  pickerStar: { padding: 2 },
  clip: { overflow: 'hidden' },
});
